import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { getPath } from '../../utils/navigation';
import { cn } from '../../lib/utils';
import { QUICKSTART_EXAMPLES } from './quickstart';
import { Input } from '../ui/input';

export const DemoSearchBar = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // Filter examples based on search input
  const filteredExamples = QUICKSTART_EXAMPLES.filter(
    (example) =>
      example.label.toLowerCase().includes(searchValue.toLowerCase()) ||
      example.value.toLowerCase().includes(searchValue.toLowerCase()),
  );

  // Simple keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredExamples.length > 0) {
        setActiveIndex((prev) =>
          prev < filteredExamples.length - 1 ? prev + 1 : 0,
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredExamples.length > 0) {
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : filteredExamples.length - 1,
        );
      }
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filteredExamples.length) {
        e.preventDefault();
        const selectedValue = filteredExamples[activeIndex].value;
        setSearchValue(selectedValue); // Set the input value first
        // Small delay to show the value in input before navigating
        setTimeout(() => {
          navigate(getPath(selectedValue));
          setSearchValue('');
          setShowExamples(false);
        }, 100);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowExamples(false);
      searchRef.current?.blur();
    } else if (e.key === '/') {
      e.preventDefault();
      setShowExamples(true);
    }
  };

  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        (!document.activeElement ||
          !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName))
      ) {
        e.preventDefault();
        searchRef.current?.focus();
        setShowExamples(true);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  const handleSearch = () => {
    if (!searchValue.trim()) return;

    navigate(getPath(searchValue));
    setSearchValue('');
    setShowExamples(false);
  };

  const handleItemSelect = (value: string) => {
    setSearchValue(value); // Set the input value first
    // Small delay to show the value in input before navigating
    setTimeout(() => {
      navigate(getPath(value));
      setSearchValue('');
      setShowExamples(false);
    }, 100);
  };

  return (
    <div className="relative z-50 mx-auto mb-8 w-full max-w-[700px] px-4">
      <div className="relative">
        <Search
          className={cn(
            'absolute left-6 top-1/2 z-10 h-5 w-5 -translate-y-1/2 transition-colors',
            isSearchFocused ? 'text-muted-500' : 'text-muted-400',
          )}
        />
        <Input
          ref={searchRef}
          type="text"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            // Reset active index to 0 whenever search changes
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search by Address / Tx Hash"
          className={cn(
            'w-full rounded-lg py-6 pl-12 text-lg shadow-md transition-all focus:border-muted-300 focus:ring-0 focus-visible:ring-0',
          )}
          onFocus={() => {
            setIsSearchFocused(true);
            setShowExamples(true);
            // Default to selecting the first item when dropdown appears
            setActiveIndex(0);
          }}
          onBlur={() => {
            setIsSearchFocused(false);
            // Small delay to allow clicking on dropdown items
            setTimeout(() => setShowExamples(false), 200);
          }}
        />
        {!isSearchFocused && (
          <div className="absolute right-6 top-1/2 flex -translate-y-1/2 items-center">
            <kbd className="rounded-sm bg-muted-100 px-2 py-0.5 text-sm font-bold text-muted-500">
              /
            </kbd>
          </div>
        )}
      </div>

      {showExamples && filteredExamples.length > 0 && (
        <div className="absolute inset-x-4 mt-1 rounded-md border border-muted-200 bg-background text-foreground shadow-md">
          <div className="max-h-[300px] overflow-y-auto rounded-md p-2">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Quickstart Examples
            </div>
            <div className="space-y-1">
              {filteredExamples.map((example, index) => (
                <div
                  key={example.value}
                  onClick={() => handleItemSelect(example.value)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                    activeIndex === index
                      ? 'bg-muted-100 font-medium'
                      : 'hover:bg-muted-50',
                  )}
                >
                  <img
                    src={example.logo}
                    alt={example.label}
                    className="h-6 w-6 rounded-full"
                  />
                  <div className="flex flex-col items-start">
                    <span>{example.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {example.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
